import { useEffect, useState, type ReactNode } from 'react'
import {
  formatDate,
  formatMoney,
  formatWeight,
  isNegative,
  parseNumberInput,
  today,
} from '../lib/format'

/* ---- figures and dates ------------------------------------------------ */

/** A date, always as "05 Sep 2026", always bidi-isolated. */
export function Dt({ value }: { value?: string | null }) {
  return <span className="dt">{formatDate(value)}</span>
}

/** A rupee figure. Red only where red means somebody owes the shop. */
export function Amt({
  value,
  tone = 'plain',
}: {
  value?: string | number | null
  tone?: 'plain' | 'balance' | 'muted'
}) {
  const className =
    tone === 'muted'
      ? 'amt amt--muted'
      : tone === 'balance'
        ? isNegative(value)
          ? 'amt amt--credit'
          : 'amt amt--debt'
        : 'amt'

  return <span className={className}>{formatMoney(value)}</span>
}

export function Wt({ value }: { value?: string | number | null }) {
  return <span className="wt">{formatWeight(value)}</span>
}

/* ---- shells ------------------------------------------------------------ */

export function Card({
  title,
  children,
  actions,
}: {
  title?: string
  children: ReactNode
  actions?: ReactNode
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {title && <h2 className="card__title">{title}</h2>}
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

/**
 * The date as a chip beside a page's name, not a labelled row in the form.
 *
 * <p>Nine entries in ten are today's, so a full field spent a whole line on a
 * question nobody is asking - and the day a thing belongs to is what the whole
 * screen is about, so it reads better at the top than buried among the boxes.
 *
 * <p>It is still a real date input underneath. Tapping it opens the phone's own
 * picker and back-dating a slip written a day late works exactly as before;
 * styling the input itself rather than triggering it from a button is
 * deliberate, because showPicker() is not supported everywhere and a chip that
 * did nothing on the shop's phone would be worse than the row it replaced.
 */
export function DateChip({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  return (
    <label className="datechip">
      <span className="datechip__face">
        {value === today() ? `آج — ${formatDate(value)}` : formatDate(value)}
      </span>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        /*
         * For engines with no stretched calendar button of their own.
         * Must run inside the tap to count as a user gesture, and is wrapped
         * because calling it where it is unsupported - or twice, when the
         * native indicator has already opened the picker - throws rather than
         * doing nothing.
         */
        onClick={(event) => {
          const input = event.currentTarget as HTMLInputElement & { showPicker?: () => void }
          try {
            input.showPicker?.()
          } catch {
            /* The native indicator has it. */
          }
        }}
        required
      />
    </label>
  )
}

export function PageHead({
  title,
  hint,
  /**
   * Sits on the title's line, at the far end. For the one control that belongs
   * beside a page's name rather than inside its form - the date on the selling
   * screen, which is what the whole page is about and is almost always today.
   */
  aside,
}: {
  title: string
  hint?: string
  aside?: ReactNode
}) {
  return (
    <div className="page-head">
      <div className="page-head__line">
        <h1>{title}</h1>
        {aside}
      </div>
      {hint && <p>{hint}</p>}
    </div>
  )
}

export function Note({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'warn' | 'error'
  children: ReactNode
}) {
  return <div className={`note note--${kind}`}>{children}</div>
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>
}

/**
 * How many rows a working list will draw before it stops.
 *
 * Three months of selling is around 1,500 lines. Drawing them all takes close to
 * two seconds on a laptop, and of that nearly a whole second is one unbroken
 * block of work: the page cannot answer a tap, scroll, or keystroke while it
 * runs. It does not read as a long list, it reads as the application having
 * died - and the phone the shop actually uses is several times slower than the
 * laptop this was measured on.
 *
 * Two hundred is far more than anybody reads. The way to see an older row is to
 * ask for its days, which is what the date boxes are for, and what the line
 * under a stopped list says out loud.
 *
 * <p><b>Never on a bill.</b> A bill is a document that gets handed over, printed
 * and sent on WhatsApp, and one that quietly leaves rows out is worse than one
 * that takes a moment to draw. {@link LongListNote} is for working lists only.
 */
export const LONG_LIST_CAP = 200

/**
 * The line under a list that stopped early. Draws nothing when it did not.
 *
 * Says the real total rather than only what is on screen, because a list that
 * silently ends is how somebody concludes a sale was never written down.
 */
export function LongListNote({ shown, total }: { shown: number; total: number }) {
  if (total <= shown) {
    return null
  }
  return (
    <p className="muted small" style={{ marginTop: 10 }}>
      کل <span className="amt">{total.toLocaleString('en-US')}</span> انٹریوں میں سے{' '}
      <span className="amt">{shown.toLocaleString('en-US')}</span> دکھائی جا رہی ہیں — باقی دیکھنے
      کے لیے تاریخ کم کر لیں۔ گنتی اور جوڑ اوپر پوری لسٹ کا ہی ہے۔
    </p>
  )
}

export function TotalBox({
  label,
  value,
  accent,
  debt,
}: {
  label: string
  value: ReactNode
  accent?: boolean
  debt?: boolean
}) {
  return (
    <div
      className={`total-box${accent ? ' total-box--accent' : ''}${debt ? ' total-box--debt' : ''}`}
    >
      <div className="total-box__label">{label}</div>
      <div className="total-box__value">{value}</div>
    </div>
  )
}

/* ---- fields ------------------------------------------------------------ */

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && <div className="field__hint">{hint}</div>}
    </div>
  )
}

/** A number box that accepts Urdu digits and hands back plain ones. */
export function NumberInput({
  value,
  onChange,
  placeholder,
  decimals = false,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  decimals?: boolean
}) {
  return (
    <input
      inputMode={decimals ? 'decimal' : 'numeric'}
      value={value}
      placeholder={placeholder}
      onChange={(event) => onChange(parseNumberInput(event.target.value))}
    />
  )
}

/* ---- editing in place --------------------------------------------------- */

/** Two-tap delete. Nothing destructive happens on a single stray touch. */
export function ConfirmDelete({
  onConfirm,
  label = 'ڈیلیٹ',
}: {
  onConfirm: () => void
  label?: string
}) {
  const [armed, setArmed] = useState(false)

  if (!armed) {
    return (
      <button type="button" className="btn btn--danger btn--small" onClick={() => setArmed(true)}>
        {label}
      </button>
    )
  }

  return (
    <span className="confirm-pair">
      <button
        type="button"
        className="btn btn--danger btn--small"
        onClick={() => {
          setArmed(false)
          onConfirm()
        }}
      >
        پکا؟ ہاں
      </button>
      <button type="button" className="btn btn--ghost btn--small" onClick={() => setArmed(false)}>
        نہیں
      </button>
    </span>
  )
}

/**
 * A figure that can be corrected where it sits.
 *
 * Haggling carries on after a line is written down, so the weight and the rate
 * both move. Committing on blur and on Enter - rather than behind an edit button
 * - is what makes correcting four lines in a row bearable on a tablet.
 */
export function NumberCell({
  value,
  onCommit,
  width = 90,
  decimals = false,
  ariaLabel,
}: {
  value: string
  /**
   * Return the save's promise and a refusal puts the box back; return nothing
   * and the box keeps whatever was typed, which is what every caller written
   * before this did.
   */
  onCommit: (value: string) => void | Promise<unknown>
  width?: number
  decimals?: boolean
  ariaLabel: string
}) {
  const [draft, setDraft] = useState(value)

  useEffect(() => setDraft(value), [value])

  /**
   * Puts the typed figure back to what the book holds when the server refuses
   * it.
   *
   * <p>Without this, a refused edit leaves the number on screen looking saved -
   * the red line says no, but the box still reads 5 where the book says 19, and
   * whoever walks away from the counter believes the 5. The reload that follows
   * a successful save cannot fix it either: the value prop comes back unchanged,
   * so nothing tells this box to think again.
   */
  async function commit() {
    const cleaned = parseNumberInput(draft)
    if (cleaned === '' || cleaned === value) {
      setDraft(value)
      return
    }
    try {
      await onCommit(cleaned)
    } catch {
      setDraft(value)
    }
  }

  return (
    <input
      className="cell-input"
      aria-label={ariaLabel}
      inputMode={decimals ? 'decimal' : 'numeric'}
      style={{ width }}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') setDraft(value)
      }}
    />
  )
}
